import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { InputMask } from '@/components/ui/InputMask';
import { Label } from '@/components/ui/Label';
import { alunoService, cursoService, cidadeService } from '@/services/api';
import { CreateAlunoDTO, UpdateAlunoDTO, Curso, Cidade } from '@/types';
import { useErrorHandler } from '@/hooks/useErrorHandler';
import { useQueryInvalidation } from '@/hooks/useQueryInvalidation';
import { SkeletonForm } from '@/components/ui/SkeletonLoader';

const alunoSchema = z.object({
  nome: z.string().min(1, 'Nome é obrigatório'),
  rg: z.string().optional(),
  cpf: z.string().min(11, 'CPF é obrigatório'),
  dataNascimento: z.string().min(1, 'Data de nascimento é obrigatória'),
  telefone1: z.string().optional(),
  telefone2: z.string().optional(),
  email: z.string().email('Email inválido').optional(),
  endereco: z.string().optional(),
  bairro: z.string().optional(),
  cep: z.string().optional(),
  cidadeId: z.number().min(1, 'Cidade é obrigatória'),
  cursoId: z.number().min(1, 'Curso é obrigatório'),
  matricula: z.string().min(1, 'Matrícula é obrigatória'),
  periodo: z.string().optional(),
});

type AlunoFormData = z.infer<typeof alunoSchema>;

export const NovoAlunoPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEditing = !!id;

  const { handleError, handleSuccess } = useErrorHandler();
  const { invalidateAlunos } = useQueryInvalidation();

  const [cursos, setCursos] = useState<Curso[]>([]);
  const [cidades, setCidades] = useState<Cidade[]>([]);
  const [cpfValue, setCpfValue] = useState('');
  const [telefone1Value, setTelefone1Value] = useState('');
  const [telefone2Value, setTelefone2Value] = useState('');
  const [cepValue, setCepValue] = useState('');

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    reset,
  } = useForm<AlunoFormData>({
    resolver: zodResolver(alunoSchema),
  });

  // Carregar cursos e cidades
  useEffect(() => {
    const loadOptions = async () => {
      try {
        const [cursosResp, cidadesResp] = await Promise.all([
          cursoService.list({ page: 1, limit: 1000 }),
          cidadeService.list({ page: 1, limit: 1000 }),
        ]);
        setCursos(cursosResp?.data || []);
        setCidades(cidadesResp?.data || []);
      } catch (e) {
        handleError(e as Error, 'Erro ao carregar opções');
      }
    };
    loadOptions();
  }, [handleError]);

  // Carregar dados do aluno se estiver editando
  const { data: alunoData, isLoading: isLoadingAluno } = useQuery({
    queryKey: ['aluno', id],
    queryFn: () => alunoService.getById(Number(id)),
    enabled: isEditing,
  });

  useEffect(() => {
    if (alunoData) {
      reset({
        nome: alunoData.nome,
        rg: alunoData.rg || '',
        cpf: alunoData.cpf,
        dataNascimento: alunoData.dataNascimento,
        telefone1: alunoData.telefone1 || '',
        telefone2: alunoData.telefone2 || '',
        email: alunoData.email || '',
        endereco: alunoData.endereco || '',
        bairro: alunoData.bairro || '',
        cep: alunoData.cep || '',
        cidadeId: alunoData.cidadeId,
        cursoId: alunoData.cursoId,
        matricula: alunoData.matricula,
        periodo: alunoData.periodo || '',
      });
      setCpfValue(alunoData.cpf || '');
      setTelefone1Value(alunoData.telefone1 || '');
      setTelefone2Value(alunoData.telefone2 || '');
      setCepValue(alunoData.cep || '');
    }
  }, [alunoData, reset]);

  const createMutation = useMutation({
    mutationFn: (data: CreateAlunoDTO) => alunoService.create(data),
    onSuccess: () => {
      invalidateAlunos();
      handleSuccess('Aluno criado com sucesso!');
      navigate('/alunos');
    },
    onError: (error: Error) => {
      handleError(error, 'Erro ao criar aluno');
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: UpdateAlunoDTO) => alunoService.update(Number(id), data),
    onSuccess: () => {
      invalidateAlunos();
      handleSuccess('Aluno atualizado com sucesso!');
      navigate('/alunos');
    },
    onError: (error: Error) => {
      handleError(error, 'Erro ao atualizar aluno');
    },
  });

  const onSubmit = async (data: AlunoFormData) => {
    if (isEditing) {
      await updateMutation.mutateAsync(data as UpdateAlunoDTO);
    } else {
      await createMutation.mutateAsync(data as CreateAlunoDTO);
    }
  };

  if (isEditing && isLoadingAluno) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Editar Aluno</h1>
          <p className="mt-1 text-sm text-gray-500">Atualize as informações do aluno</p>
        </div>
        <Card>
          <CardHeader>
            <CardTitle>Dados do Aluno</CardTitle>
          </CardHeader>
          <CardContent>
            <SkeletonForm />
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">
          {isEditing ? 'Editar Aluno' : 'Novo Aluno'}
        </h1>
        <p className="mt-1 text-sm text-gray-500">
          {isEditing ? 'Atualize as informações do aluno' : 'Preencha os dados do novo aluno'}
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Dados Pessoais</CardTitle>
          <CardDescription>Informações básicas do aluno</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="nome">Nome Completo *</Label>
                <Input
                  id="nome"
                  {...register('nome')}
                  placeholder="Digite o nome completo"
                  className={errors.nome ? 'border-red-500' : ''}
                />
                {errors.nome && (
                  <p className="mt-1 text-sm text-red-600">{errors.nome.message}</p>
                )}
              </div>

              <div>
                <Label htmlFor="rg">RG</Label>
                <Input
                  id="rg"
                  {...register('rg')}
                  placeholder="Digite o RG"
                />
              </div>

              <div>
                <Label htmlFor="cpf">CPF *</Label>
                <InputMask
                  id="cpf"
                  mask="cpf"
                  value={cpfValue}
                  onChange={(masked, raw) => {
                    setCpfValue(masked);
                    setValue('cpf', raw);
                  }}
                  placeholder="000.000.000-00"
                  error={!!errors.cpf}
                />
                {errors.cpf && (
                  <p className="mt-1 text-sm text-red-600">{errors.cpf.message}</p>
                )}
              </div>

              <div>
                <Label htmlFor="dataNascimento">Data de Nascimento *</Label>
                <Input
                  id="dataNascimento"
                  type="date"
                  {...register('dataNascimento')}
                  className={errors.dataNascimento ? 'border-red-500' : ''}
                />
                {errors.dataNascimento && (
                  <p className="mt-1 text-sm text-red-600">{errors.dataNascimento.message}</p>
                )}
              </div>

              <div>
                <Label htmlFor="telefone1">Telefone 1</Label>
                <InputMask
                  id="telefone1"
                  mask="phone"
                  value={telefone1Value}
                  onChange={(masked, raw) => {
                    setTelefone1Value(masked);
                    setValue('telefone1', raw);
                  }}
                  placeholder="(00) 00000-0000"
                />
              </div>

              <div>
                <Label htmlFor="telefone2">Telefone 2</Label>
                <InputMask
                  id="telefone2"
                  mask="phone"
                  value={telefone2Value}
                  onChange={(masked, raw) => {
                    setTelefone2Value(masked);
                    setValue('telefone2', raw);
                  }}
                  placeholder="(00) 00000-0000"
                />
              </div>

              <div className="md:col-span-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  {...register('email')}
                  placeholder="aluno@email.com"
                  className={errors.email ? 'border-red-500' : ''}
                />
                {errors.email && (
                  <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
                )}
              </div>
            </div>

            <div className="pt-4 border-t">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Endereço</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <Label htmlFor="endereco">Endereço</Label>
                  <Input
                    id="endereco"
                    {...register('endereco')}
                    placeholder="Rua, número, complemento"
                  />
                </div>

                <div>
                  <Label htmlFor="bairro">Bairro</Label>
                  <Input
                    id="bairro"
                    {...register('bairro')}
                    placeholder="Digite o bairro"
                  />
                </div>

                <div>
                  <Label htmlFor="cep">CEP</Label>
                  <InputMask
                    id="cep"
                    mask="cep"
                    value={cepValue}
                    onChange={(masked, raw) => {
                      setCepValue(masked);
                      setValue('cep', raw);
                    }}
                    placeholder="00000-000"
                  />
                </div>

                <div>
                  <Label htmlFor="cidadeId">Cidade *</Label>
                  <select
                    id="cidadeId"
                    {...register('cidadeId', { valueAsNumber: true })}
                    className="flex h-10 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm"
                  >
                    <option value="">Selecione uma cidade</option>
                    {cidades.map((cidade) => (
                      <option key={cidade.id} value={cidade.id}>
                        {cidade.nome} - {cidade.uf}
                      </option>
                    ))}
                  </select>
                  {errors.cidadeId && (
                    <p className="mt-1 text-sm text-red-600">{errors.cidadeId.message}</p>
                  )}
                </div>
              </div>
            </div>

            <div className="pt-4 border-t">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Dados Acadêmicos</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="cursoId">Curso *</Label>
                  <select
                    id="cursoId"
                    {...register('cursoId', { valueAsNumber: true })}
                    className="flex h-10 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm"
                  >
                    <option value="">Selecione um curso</option>
                    {cursos.map((curso) => (
                      <option key={curso.id} value={curso.id}>
                        {curso.nome}
                      </option>
                    ))}
                  </select>
                  {errors.cursoId && (
                    <p className="mt-1 text-sm text-red-600">{errors.cursoId.message}</p>
                  )}
                </div>

                <div>
                  <Label htmlFor="matricula">Matrícula *</Label>
                  <Input
                    id="matricula"
                    {...register('matricula')}
                    placeholder="Digite a matrícula"
                    className={errors.matricula ? 'border-red-500' : ''}
                  />
                  {errors.matricula && (
                    <p className="mt-1 text-sm text-red-600">{errors.matricula.message}</p>
                  )}
                </div>

                <div>
                  <Label htmlFor="periodo">Período</Label>
                  <Input
                    id="periodo"
                    {...register('periodo')}
                    placeholder="Ex: 3º Período"
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-end space-x-3 pt-6 border-t">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/alunos')}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={createMutation.isPending || updateMutation.isPending}
              >
                {createMutation.isPending || updateMutation.isPending
                  ? 'Salvando...'
                  : isEditing
                  ? 'Atualizar Aluno'
                  : 'Cadastrar Aluno'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};